import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { Section, Eyebrow, H1, Lead, ButtonPrimary } from "../components/ui-kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "school", label: "School Empanelment" },
  { id: "partner", label: "eSports Partner" },
  { id: "general", label: "General Inquiry" },
];

export default function Contact() {
  const search = useMemo(() => new URLSearchParams(window.location.search), []);
  const presetType = search.get("type");
  const [type, setType] = useState(TYPES.find((t) => t.id === presetType)?.id || "school");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    role: "",
    phone: "",
    region: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lead_type: type });
      toast.success("Signal received. Mission Control will reach out within 48 hours.");
      setForm({ full_name: "", email: "", organization: "", role: "", phone: "", region: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Submission failed. Check fields.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Section className="!pt-16">
        <Eyebrow>Mission Control</Eyebrow>
        <H1>Talk to the operators.</H1>
        <Lead className="mt-6">
          One inbox for empanelment, partnerships, press, and procurement. We respond in under 48 hours — earlier for state-level deployments.
        </Lead>
      </Section>

      <Section className="!pt-0">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-5 space-y-4">
            {[
              { icon: Mail, t: "Email", v: "kmukherjee@perforange.com" },
              { icon: Phone, t: "Direct", v: "+91 84310 25774" },
              { icon: MapPin, t: "HQ", v: "Mumbai, India" },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="glass rounded-xl p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FF8C00]/10 border border-[#FF8C00]/30 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#FF8C00]" />
                  </div>
                  <p className="font-bold text-white text-sm sm:text-base break-all">{c.v}</p>
                </div>
              );
            })}
            <div className="glass-strong rounded-xl p-5">
              <p className="text-xs uppercase tracking-widest text-[#00FFD0] font-mono-tech mb-2">For Principals</p>
              <p className="text-sm text-[#D4D4D8]">Empanelment SLAs: 14 days for single-lab onboarding, 45 days for district clusters, 90 days for state deployments.</p>
            </div>
          </div>

          <div className="md:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="lead-form"
              className="glass-strong rounded-2xl p-6 md:p-8 space-y-5"
            >
              <div>
                <p className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-3">I am here as a...</p>
                <div className="flex flex-wrap gap-2">
                  {TYPES.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => setType(t.id)}
                      data-testid={`type-${t.id}`}
                      className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                        type === t.id
                          ? "bg-[#FF8C00] text-[#0A0A0C]"
                          : "bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-white"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *" name="full_name" value={form.full_name} onChange={onChange} required />
                <Field label="Email *" type="email" name="email" value={form.email} onChange={onChange} required />
                <Field label={type === "school" ? "School / Board *" : "Organization"} name="organization" value={form.organization} onChange={onChange} required={type === "school"} />
                <Field label="Role / Designation" name="role" value={form.role} onChange={onChange} />
                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                <Field label="State / Region" name="region" value={form.region} onChange={onChange} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">Message</label>
                <textarea
                  name="message"
                  data-testid="input-message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell us about your lab, cohort size, timeline..."
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-sm resize-none"
                />
              </div>

              <ButtonPrimary type="submit" disabled={loading} data-testid="submit-lead" className="w-full sm:w-auto">
                {loading ? "Transmitting..." : "Send to Mission Control"} <ArrowRight className="w-4 h-4" />
              </ButtonPrimary>
            </form>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`input-${name}`}
        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-sm"
      />
    </div>
  );
}
